import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChatPopup } from '../../contexts/ChatPopupContext';
import { jobAPI } from '../../services/api';
import JobShareModal from './JobShareModal';
import JobDescriptionContent from './JobDescriptionContent';
import './JobDetailPanel.css';

const JobDetailPanel = ({ job, applications = [], onJobUpdate, isAppliedJobsView = false, onEdit }) => {
  const { user } = useAuth();
  const { openChatPopup } = useChatPopup();
  const [jobDetails, setJobDetails] = useState(job || null);
  const [loading, setLoading] = useState(false);
  
  // Check for initial tab from sessionStorage (set when navigating from notification)
  const getInitialTab = () => {
    if (job?.id) {
      const storedTab = sessionStorage.getItem(`job-${job.id}-initialTab`);
      if (storedTab) {
        sessionStorage.removeItem(`job-${job.id}-initialTab`); // Clear after use
        return storedTab;
      }
    }
    return 'details';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [updating, setUpdating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionMessage, setActionMessage] = useState({ type: '', text: '' });
  const [applicationsList, setApplicationsList] = useState(applications);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [applicationFilters, setApplicationFilters] = useState({ status: 'all' });
  const [applicationPagination, setApplicationPagination] = useState(null);
  const [currentApplicationPage, setCurrentApplicationPage] = useState(1);
  const [showShareModal, setShowShareModal] = useState(false);
  const [applicantNameSearch, setApplicantNameSearch] = useState('');
  const [applicantSearchDebounceTimer, setApplicantSearchDebounceTimer] = useState(null);
  const [statusCounts, setStatusCounts] = useState({ all: 0, pending: 0, reviewed: 0, shortlisted: 0, rejected: 0 });
  const [editingNotes, setEditingNotes] = useState({});
  const [notesValue, setNotesValue] = useState({});
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectingApplicationId, setRejectingApplicationId] = useState(null);
  const [selectedRejectionReason, setSelectedRejectionReason] = useState('');
  const [customRejectionMessage, setCustomRejectionMessage] = useState('');
  const [userApplication, setUserApplication] = useState(null);
  const [loadingUserApplication, setLoadingUserApplication] = useState(false);

  // Track previous job ID to prevent unnecessary refetches
  const prevJobIdRef = useRef(null);

  useEffect(() => {
    // Update jobDetails when job prop changes
    if (job) {
      const jobId = job.id;
      const jobIdChanged = prevJobIdRef.current !== jobId;
      
      if (jobIdChanged) {
        prevJobIdRef.current = jobId;
        setJobDetails(job);
        // Check for initial tab when job changes (from notification redirect)
        const storedTab = sessionStorage.getItem(`job-${job.id}-initialTab`);
        if (storedTab) {
          setActiveTab(storedTab);
          sessionStorage.removeItem(`job-${job.id}-initialTab`); // Clear after use
        }
        // Fetch full job details if job has an ID
        if (job.id) {
          fetchJobDetails();
        } else {
          console.warn('JobDetailPanel: Job object missing ID', job);
          setLoading(false);
        }
      } else {
        // Job ID hasn't changed, but job data might have been updated
        // Check if the job object has more complete data (e.g., from API response)
        // If the incoming job has more fields than current jobDetails, update and fetch
        const hasMoreData = job.updated_at && (!jobDetails?.updated_at || 
          new Date(job.updated_at) > new Date(jobDetails.updated_at));
        
        // Always update jobDetails with the new job data
        setJobDetails(job);
        
        // If the job appears to be updated (has updated_at timestamp), fetch fresh data
        // This ensures we get the latest from server after an update
        if (job.id && (hasMoreData || !jobDetails)) {
          fetchJobDetails();
        }
      }
    } else {
      prevJobIdRef.current = null;
      setJobDetails(null);
      setLoading(false);
    }
    
    // Update applications list when applications prop changes
    setApplicationsList(applications);
    
    // When viewing applied jobs, ensure we're on details tab (no applications tab)
    if (isAppliedJobsView && activeTab === 'applications') {
      setActiveTab('details');
    }

    // Fetch user's application when viewing as applicant
    if (isAppliedJobsView && job?.id && user) {
      fetchUserApplication();
    }
  }, [job?.id, job, isAppliedJobsView, applications, user]);

  const fetchJobDetails = async () => {
    if (!job || !job.id) {
      console.warn('Cannot fetch job details: job or job.id is missing', job);
      return;
    }
    
    setLoading(true);
    try {
      const response = await jobAPI.get(job.id);
      if (response.success) {
        setJobDetails(response.data);
      }
    } catch (err) {
      console.error('Error fetching job details:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch status counts for all applications
  const fetchUserApplication = async () => {
    if (!jobDetails || !jobDetails.id || !user) return;

    setLoadingUserApplication(true);
    try {
      // Fetch all user applications and find the one for this job
      const response = await jobAPI.getMyApplications({ per_page: 100 });
      if (response.success) {
        const apps = response.data?.data || response.data || [];
        const appForThisJob = apps.find(app => app.job_id === jobDetails.id || app.job?.id === jobDetails.id);
        if (appForThisJob) {
          setUserApplication(appForThisJob);
        } else {
          setUserApplication(null);
        }
      }
    } catch (err) {
      console.error('Error fetching user application:', err);
      setUserApplication(null);
    } finally {
      setLoadingUserApplication(false);
    }
  };

  const fetchStatusCounts = async () => {
    if (!jobDetails || !jobDetails.id) return;
    
    try {
      // Fetch all applications without pagination to get counts
      const response = await jobAPI.getApplications(jobDetails.id, { per_page: 1, page: 1 });
      if (response.success) {
        const paginatedData = response.data;
        const total = paginatedData?.total || 0;
        
        // Fetch counts for each status
        const statuses = ['pending', 'reviewed', 'shortlisted', 'rejected'];
        const counts = { all: total };
        
        // Fetch each status count
        const countPromises = statuses.map(async (status) => {
          try {
            const statusResponse = await jobAPI.getApplications(jobDetails.id, { status, per_page: 1, page: 1 });
            if (statusResponse.success) {
              counts[status] = statusResponse.data?.total || 0;
            }
          } catch (err) {
            counts[status] = 0;
          }
        });
        
        await Promise.all(countPromises);
        setStatusCounts(counts);
      }
    } catch (err) {
      console.error('Error fetching status counts:', err);
    }
  };

  const fetchApplications = async (page = 1, statusFilter = applicationFilters.status, applicantName = applicantNameSearch) => {
    if (!jobDetails || !jobDetails.id) {
      return;
    }

    const actingUserId = user?.acting_user_id ?? user?.id;
    const isJobOwner = user && (
      jobDetails.posted_by_user_id === actingUserId ||
      jobDetails.posted_by?.id === actingUserId ||
      jobDetails.posted_by_user_id?.toString() === String(actingUserId)
    );

    // Only fetch if user is job owner and not in applied jobs view
    if (!isJobOwner || isAppliedJobsView) {
      return;
    }

    setLoadingApplications(true);
    try {
      const params = {
        page: page,
        per_page: 10,
        ...(statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(applicantName && applicantName.trim() ? { applicant_name: applicantName.trim() } : {})
      };
      
      const response = await jobAPI.getApplications(jobDetails.id, params);
      if (response.success) {
        // Handle paginated response structure
        const paginatedData = response.data;
        const apps = paginatedData?.data || paginatedData || [];
        setApplicationsList(apps);
        setApplicationPagination(paginatedData);
        setCurrentApplicationPage(page);
        // Update job details with latest application count from pagination (only if showing all)
        if (statusFilter === 'all' && paginatedData?.total !== undefined) {
          setJobDetails(prev => ({
            ...prev,
            application_count: paginatedData.total
          }));
        }
        
        // Update status counts when fetching all applications
        if (statusFilter === 'all') {
          fetchStatusCounts();
        }
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      setApplicationsList([]);
      setApplicationPagination(null);
    } finally {
      setLoadingApplications(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Fetch applications when switching to applications tab
    if (tab === 'applications') {
      fetchApplications(1, applicationFilters.status, applicantNameSearch);
      fetchStatusCounts();
    }
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...applicationFilters,
      [filterName]: value
    };
    setApplicationFilters(newFilters);
    setCurrentApplicationPage(1);
    fetchApplications(1, newFilters.status, applicantNameSearch);
  };

  const handlePageChange = (newPage) => {
    fetchApplications(newPage, applicationFilters.status, applicantNameSearch);
  };

  // Debounced search effect for applicant name
  useEffect(() => {
    if (!jobDetails || !jobDetails.id) return;

    const actingUserId = user?.acting_user_id ?? user?.id;
    const isJobOwner = user && (
      jobDetails.posted_by_user_id === actingUserId ||
      jobDetails.posted_by?.id === actingUserId ||
      jobDetails.posted_by_user_id?.toString() === String(actingUserId)
    );

    // Only search if user is job owner and not in applied jobs view
    if (!isJobOwner || isAppliedJobsView || activeTab !== 'applications') {
      return;
    }

    // Reset to first page when search changes
    setCurrentApplicationPage(1);
    
    // Debounce the search to avoid too many requests
    const debounceTimer = setTimeout(() => {
      fetchApplications(1, applicationFilters.status, applicantNameSearch);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantNameSearch, activeTab, applicationFilters.status]);

  const handleRejectClick = (applicationId) => {
    setRejectingApplicationId(applicationId);
    setSelectedRejectionReason('');
    setCustomRejectionMessage('');
    setShowRejectionModal(true);
  };

  const handleRejectionSubmit = async () => {
    if (!jobDetails || !jobDetails.id || !rejectingApplicationId) return;

    // Validate that either predefined reason or custom message is provided
    if (!selectedRejectionReason && !customRejectionMessage.trim()) {
      showMessage('error', 'Please select a reason or provide a custom message');
      return;
    }

    // Combine predefined reason and custom message
    let rejectionReason = '';
    if (selectedRejectionReason && customRejectionMessage.trim()) {
      rejectionReason = `${selectedRejectionReason}\n\n${customRejectionMessage.trim()}`;
    } else if (selectedRejectionReason) {
      rejectionReason = selectedRejectionReason;
    } else {
      rejectionReason = customRejectionMessage.trim();
    }

    setUpdatingStatus(prev => ({ ...prev, [rejectingApplicationId]: true }));
    try {
      const response = await jobAPI.updateApplicationStatus(
        jobDetails.id, 
        rejectingApplicationId, 
        'rejected',
        null, // notes
        rejectionReason // rejection_reason
      );
      if (response.success) {
        showMessage('success', 'Application rejected successfully');
        setShowRejectionModal(false);
        setRejectingApplicationId(null);
        setSelectedRejectionReason('');
        setCustomRejectionMessage('');
        // Refresh applications to get updated data with current filters
        fetchApplications(currentApplicationPage, applicationFilters.status, applicantNameSearch);
        // Refresh status counts
        fetchStatusCounts();
      } else {
        showMessage('error', response.message || 'Failed to reject application');
      }
    } catch (error) {
      console.error('Error rejecting application:', error);
      showMessage('error', error.message || 'Failed to reject application');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [rejectingApplicationId]: false }));
    }
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    if (!jobDetails || !jobDetails.id) return;

    // If rejecting, show modal instead
    if (newStatus === 'rejected') {
      handleRejectClick(applicationId);
      return;
    }

    setUpdatingStatus(prev => ({ ...prev, [applicationId]: true }));
    try {
      const response = await jobAPI.updateApplicationStatus(jobDetails.id, applicationId, newStatus);
      if (response.success) {
        showMessage('success', `Application marked as ${newStatus}`);
        // Refresh applications to get updated data with current filters
        fetchApplications(currentApplicationPage, applicationFilters.status, applicantNameSearch);
        // Refresh status counts
        fetchStatusCounts();
      } else {
        showMessage('error', response.message || 'Failed to update application status');
      }
    } catch (error) {
      console.error('Error updating application status:', error);
      showMessage('error', error.message || 'Failed to update application status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  const handleNotesUpdate = async (applicationId, notes) => {
    if (!jobDetails || !jobDetails.id) return;

    setUpdatingStatus(prev => ({ ...prev, [applicationId]: true }));
    try {
      const response = await jobAPI.updateApplicationNotes(jobDetails.id, applicationId, notes);
      if (response.success) {
        showMessage('success', 'Notes updated successfully');
        // Update local state
        setApplicationsList(prev => prev.map(app => 
          app.id === applicationId ? { ...app, notes: notes } : app
        ));
        setEditingNotes(prev => ({ ...prev, [applicationId]: false }));
      } else {
        showMessage('error', response.message || 'Failed to update notes');
      }
    } catch (error) {
      console.error('Error updating notes:', error);
      showMessage('error', error.message || 'Failed to update notes');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  const handleStartChat = (application) => {
    if (!application.user || !jobDetails) return;
    
    // Construct job link - prefer share_token if available, otherwise use job ID
    let jobLink = '';
    if (jobDetails.share_token) {
      jobLink = `${window.location.origin}/job/${jobDetails.share_token}`;
    } else if (jobDetails.id) {
      jobLink = `${window.location.origin}/vacancies/${jobDetails.id}`;
    }
    
    // Create a pre-filled message referencing the job with nicely formatted link
    let initialMessage = `Hi ${application.user.name || 'there'}, I'd like to discuss your application for the "${jobDetails.title}" position.`;
    if (jobLink) {
      initialMessage += `\n\nView Job: ${jobLink}`;
    }
    
    openChatPopup(
      application.user.id,
      application.user.name || 'User',
      application.user.profile_photo_url || null,
      null, // initialRoom - will be created when first message is sent
      initialMessage // Pre-filled message with job reference and link
    );
  };

  const handleViewProfile = (username) => {
    if (username) {
      const profileUrl = `/talent/${username}`;
      console.log('Opening profile:', profileUrl);
      window.open(profileUrl, '_blank', 'noopener,noreferrer');
    } else {
      console.warn('Cannot view profile: username is missing');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatSalary = () => {
    if (!jobDetails.salary_min && !jobDetails.salary_max) {
      return 'Not specified';
    }
    
    const currency = jobDetails.salary_currency || 'USD';
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency;
    const period = jobDetails.salary_period === 'yearly' ? 'year' : 
                   jobDetails.salary_period === 'monthly' ? 'month' :
                   jobDetails.salary_period === 'hourly' ? 'hour' : jobDetails.salary_period;
    
    if (jobDetails.salary_min && jobDetails.salary_max) {
      return `${symbol}${jobDetails.salary_min.toLocaleString()} - ${symbol}${jobDetails.salary_max.toLocaleString()} per ${period}`;
    } else if (jobDetails.salary_min) {
      return `${symbol}${jobDetails.salary_min.toLocaleString()}+ per ${period}`;
    }
    return 'Not specified';
  };

  const showMessage = (type, text) => {
    setActionMessage({ type, text });
    setTimeout(() => setActionMessage({ type: '', text: '' }), 5000);
  };


  const handleCloseJob = async () => {
    setUpdating(true);
    try {
      const response = await jobAPI.update(jobDetails.id, { 
        status: 'closed',
        is_active: false 
      });
      
      if (response.success) {
        setJobDetails(response.data);
        showMessage('success', 'Job has been closed and will no longer receive applications');
        if (onJobUpdate) {
          onJobUpdate();
        }
      } else {
        showMessage('error', response.message || 'Failed to close job');
      }
    } catch (error) {
      console.error('Error closing job:', error);
      showMessage('error', error.message || 'Failed to close job');
    } finally {
      setUpdating(false);
    }
  };

  const handleReopenJob = async () => {
    setUpdating(true);
    try {
      const response = await jobAPI.update(jobDetails.id, { 
        status: 'published',
        is_active: true 
      });
      
      if (response.success) {
        setJobDetails(response.data);
        showMessage('success', 'Job has been reopened and is now receiving applications');
        if (onJobUpdate) {
          onJobUpdate();
        }
      } else {
        showMessage('error', response.message || 'Failed to reopen job');
      }
    } catch (error) {
      console.error('Error reopening job:', error);
      showMessage('error', error.message || 'Failed to reopen job');
    } finally {
      setUpdating(false);
    }
  };

  const handlePublishJob = async () => {
    setUpdating(true);
    try {
      const response = await jobAPI.updateStatus(jobDetails.id, 'published', true);
      
      if (response.success) {
        setJobDetails(response.data);
        showMessage('success', 'Job has been published successfully! You will receive a confirmation email shortly.');
        if (onJobUpdate) {
          onJobUpdate();
        }
      } else {
        showMessage('error', response.message || 'Failed to publish job');
      }
    } catch (error) {
      console.error('Error publishing job:', error);
      showMessage('error', error.message || 'Failed to publish job');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteJob = async () => {
    setUpdating(true);
    try {
      const response = await jobAPI.delete(jobDetails.id);
      
      if (response.success) {
        showMessage('success', 'Job has been deleted successfully');
        setShowDeleteConfirm(false);
        // Call onJobUpdate to refresh the list, which will handle the removal
        if (onJobUpdate) {
          onJobUpdate();
        }
        // Clear the selected job after a short delay
        setTimeout(() => {
          setJobDetails(null);
        }, 1000);
      } else {
        showMessage('error', response.message || 'Failed to delete job');
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      showMessage('error', error.message || 'Failed to delete job');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="job-detail-loading">
        <div className="job-detail-spinner"></div>
        <span>Loading job details...</span>
      </div>
    );
  }

  if (!jobDetails) {
    return (
      <div className="job-detail-panel">
        <div className="job-deleted-message">
          <p>This job has been deleted.</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="job-detail-panel">
      {/* Non-Compliance Warning */}
      {jobDetails.is_non_compliant && jobDetails.non_compliance_reason && (
        <div className="job-non-compliance-alert">
          <div className="alert-content">
            <div className="alert-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </div>
            <div className="alert-text">
              <h4>Job Removed from Public Access</h4>
              <p><strong>Reason:</strong> {jobDetails.non_compliance_reason}</p>
              {jobDetails.removed_at && (
                <p className="removed-date">Removed on: {new Date(jobDetails.removed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {actionMessage.text && (
        <div className={`job-action-message ${actionMessage.type}`}>
          {actionMessage.text}
        </div>
      )}
      
      <div className="job-detail-header">
        <div className="job-detail-title-section">
          <h1 className="job-detail-title">{jobDetails.title}</h1>
          <div className="job-detail-meta">
            {(() => {
              const posterUsername = jobDetails.posted_by?.username || jobDetails.postedBy?.username;
              const label = jobDetails.company?.name || (jobDetails.posted_by?.name || jobDetails.postedBy?.name ? `Posted by ${jobDetails.posted_by?.name || jobDetails.postedBy?.name}` : 'Your Company');
              return posterUsername ? (
                <a href={`/talent/${posterUsername}`} target="_blank" rel="noopener noreferrer" className="job-detail-company job-detail-company-link">{label}</a>
              ) : (
                <span className="job-detail-company">{label}</span>
              );
            })()}
            {jobDetails.work_type && (
              <>
                <span className="job-detail-separator">•</span>
                <span className="job-detail-type">{jobDetails.work_type}</span>
              </>
            )}
            {jobDetails.job_type && (
              <>
                <span className="job-detail-separator">•</span>
                <span className="job-detail-type">{jobDetails.job_type}</span>
              </>
            )}
          </div>
        </div>
        <div className="job-detail-header-right">
          <div className="job-detail-status">
            {jobDetails.deleted_at ? (
              <span className="job-status-badge deleted">Deleted</span>
            ) : jobDetails.is_non_compliant ? (
              <span className="job-status-badge non-compliant">Non-Compliant</span>
            ) : jobDetails.status === 'closed' ? (
              <span className="job-status-badge closed">Completed</span>
            ) : jobDetails.status === 'draft' ? (
              <span className="job-status-badge draft">Draft</span>
            ) : jobDetails.is_active && jobDetails.status === 'published' ? (
              <span className="job-status-badge active">Active</span>
            ) : (
              <span className="job-status-badge inactive">Inactive</span>
            )}
          </div>
          
          {/* Check if user is job owner */}
          {(() => {
            const actingUserId = user?.acting_user_id ?? user?.id;
            const isJobOwner = user && (
              jobDetails.posted_by_user_id === actingUserId ||
              jobDetails.posted_by?.id === actingUserId ||
              jobDetails.posted_by_user_id?.toString() === String(actingUserId)
            );

            // If viewing applied jobs and user is not the owner, show "Applied" status
            if (isAppliedJobsView && !isJobOwner) {
              return (
                <div className="applied-status-badge">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  <span>Applied</span>
                </div>
              );
            }

            // If user is job owner, show action buttons
            if (isJobOwner && !isAppliedJobsView) {
              return (
                <div className="job-controls-actions-header">
                  {!jobDetails.deleted_at && onEdit && (
                    <button
                      onClick={() => onEdit(jobDetails)}
                      disabled={updating}
                      className="job-control-btn-header edit-btn"
                      title="Edit this job"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                      </svg>
                    </button>
                  )}
                  {jobDetails.status === 'draft' ? (
                    <button
                      onClick={handlePublishJob}
                      disabled={updating}
                      className="job-control-btn-header publish-btn"
                      title="Publish this job to make it visible to job seekers"
                      style={{ backgroundColor: '#28a745', color: 'white' }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                      </svg>
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => setShowShareModal(true)}
                        className="job-control-btn-header share-btn"
                        title="Share this job"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                        </svg>
                      </button>
                      {jobDetails.deleted_at ? null : jobDetails.status === 'closed' ? (
                        <button
                          onClick={handleReopenJob}
                          disabled={updating}
                          className="job-control-btn-header reopen-btn"
                          title="Reopen job to start receiving applications again"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="5 3 19 12 5 21 5 3"></polygon>
                          </svg>
                        </button>
                      ) : (
                        <button
                          onClick={handleCloseJob}
                          disabled={updating}
                          className="job-control-btn-header close-btn"
                          title="Close job - stops receiving applications"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                          </svg>
                        </button>
                      )}
                    </>
                  )}

                  {!jobDetails.deleted_at && (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={updating}
                      className="job-control-btn-header delete-btn"
                      title="Permanently delete this job"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  )}
                </div>
              );
            }

            // Default: no action buttons (for applied jobs when user is owner, or other cases)
            return null;
          })()}
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="delete-confirm-modal">
          <div className="delete-confirm-content">
            <h3>Delete Job?</h3>
            <p>Are you sure you want to delete "{jobDetails.title}"? This action cannot be undone.</p>
            <div className="delete-confirm-actions">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="delete-cancel-btn"
                disabled={updating}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteJob}
                className="delete-confirm-btn"
                disabled={updating}
              >
                {updating ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

        <div className="job-detail-tabs">
          <button
            className={`job-detail-tab ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => handleTabChange('details')}
          >
            Details
          </button>
          {/* Only show Applications tab if user is viewing their own posted jobs */}
          {!isAppliedJobsView && (() => {
            const actingUserId = user?.acting_user_id ?? user?.id;
            const isJobOwner = user && (
              jobDetails.posted_by_user_id === actingUserId ||
              jobDetails.posted_by?.id === actingUserId ||
              jobDetails.posted_by_user_id?.toString() === String(actingUserId)
            );
            return isJobOwner ? (
              <button
                className={`job-detail-tab ${activeTab === 'applications' ? 'active' : ''}`}
                onClick={() => handleTabChange('applications')}
              >
                Applications ({jobDetails.application_count || 0})
              </button>
            ) : null;
          })()}
        </div>

      <div className="job-detail-content">
        {activeTab === 'details' ? (
          <div className="job-detail-info">
            {/* User Application Status - Only show when viewing as applicant */}
            {isAppliedJobsView && userApplication && (
              <div className="job-detail-section">
                <h3>Your Application Status</h3>
                <div className="user-application-status-card">
                  <div className="user-application-status-header">
                    <span className={`application-status ${userApplication.status}`}>
                      {userApplication.status === 'pending' ? 'Pending Review' :
                       userApplication.status === 'reviewed' ? 'Under Review' :
                       userApplication.status === 'shortlisted' ? 'Shortlisted' :
                       userApplication.status === 'rejected' ? 'Not Selected' :
                       userApplication.status}
                    </span>
                    <span className="application-date">Applied on {formatDate(userApplication.applied_at)}</span>
                  </div>
                  
                  {userApplication.status === 'rejected' && userApplication.rejection_reason && (
                    <div className="rejection-reason-display">
                      <h4 className="rejection-reason-title">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }}>
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        Feedback from Employer
                      </h4>
                      <div className="rejection-reason-content">
                        {userApplication.rejection_reason.split('\n\n').map((paragraph, index) => (
                          <p key={index} style={{ margin: index > 0 ? '12px 0 0 0' : '0', whiteSpace: 'pre-wrap' }}>
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {userApplication.status === 'shortlisted' && (
                    <div className="shortlisted-message">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle', color: '#1e40af' }}>
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      <span>Congratulations! Your application has been shortlisted. The employer may contact you soon.</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="job-detail-section">
              <h3>Job Description</h3>
              <div className="job-detail-text">
                <JobDescriptionContent content={jobDetails.description} />
              </div>
            </div>

            {jobDetails.requirements && (
              <div className="job-detail-section">
                <h3>Requirements</h3>
                <div className="job-detail-text">
                  <JobDescriptionContent content={jobDetails.requirements} emptyMessage="No requirements provided" />
                </div>
              </div>
            )}

            <div className="job-detail-section">
              <h3>Job Information</h3>
              <div className="job-info-grid">
                <div className="job-info-item">
                  <span className="job-info-label">Salary</span>
                  <span className="job-info-value">{formatSalary()}</span>
                </div>
                {jobDetails.experience_level && (
                  <div className="job-info-item">
                    <span className="job-info-label">Experience Level</span>
                    <span className="job-info-value">{jobDetails.experience_level}</span>
                  </div>
                )}
                {jobDetails.location && (
                  <div className="job-info-item">
                    <span className="job-info-label">Location</span>
                    <span className="job-info-value">{jobDetails.location}</span>
                  </div>
                )}
                {jobDetails.timezone && (
                  <div className="job-info-item">
                    <span className="job-info-label">Timezone</span>
                    <span className="job-info-value">{jobDetails.timezone}</span>
                  </div>
                )}
                {jobDetails.application_deadline && (
                  <div className="job-info-item">
                    <span className="job-info-label">Application Deadline</span>
                    <span className="job-info-value">{formatDate(jobDetails.application_deadline)}</span>
                  </div>
                )}
                <div className="job-info-item">
                  <span className="job-info-label">Posted</span>
                  <span className="job-info-value">{formatDate(jobDetails.created_at)}</span>
                </div>
                <div className="job-info-item">
                  <span className="job-info-label">Status</span>
                  <span className="job-info-value">
                    {jobDetails.deleted_at 
                      ? 'Deleted' 
                      : jobDetails.status === 'closed' 
                        ? 'Completed' 
                        : jobDetails.status === 'draft'
                          ? 'Draft'
                          : jobDetails.is_active && jobDetails.status === 'published' 
                            ? 'Active' 
                            : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {jobDetails.skills_required && jobDetails.skills_required.length > 0 && (
              <div className="job-detail-section">
                <h3>Required Skills</h3>
                <div className="job-skills-list">
                  {jobDetails.skills_required.map((skill, index) => (
                    <span key={index} className="job-skill-tag">{skill}</span>
                  ))}
                </div>
              </div>
            )}

          </div>
        ) : (
          <div className="job-applications-list">
            {/* Applications Filters */}
            <div className="applications-filters">
              {/* Search by Applicant Name */}
              <div className="applications-search-group">
                <div className="applications-search-input-wrapper">
                  <svg 
                    className="applications-search-icon" 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  <input
                    type="text"
                    className="applications-search-input"
                    placeholder="Search by applicant name or email..."
                    value={applicantNameSearch}
                    onChange={(e) => {
                      setApplicantNameSearch(e.target.value);
                      setCurrentApplicationPage(1);
                    }}
                  />
                  {applicantNameSearch && (
                    <button
                      className="applications-search-clear"
                      onClick={() => {
                        setApplicantNameSearch('');
                        setCurrentApplicationPage(1);
                      }}
                      title="Clear search"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              
              {/* Status Filter Tabs */}
              <div className="applications-status-tabs">
                <button
                  className={`status-tab ${applicationFilters.status === 'all' ? 'active' : ''}`}
                  onClick={() => handleFilterChange('status', 'all')}
                >
                  All <span className="status-count">({statusCounts.all})</span>
                </button>
                <button
                  className={`status-tab ${applicationFilters.status === 'pending' ? 'active' : ''}`}
                  onClick={() => handleFilterChange('status', 'pending')}
                >
                  New <span className="status-count">({statusCounts.pending})</span>
                </button>
                <button
                  className={`status-tab ${applicationFilters.status === 'shortlisted' ? 'active' : ''}`}
                  onClick={() => handleFilterChange('status', 'shortlisted')}
                >
                  Shortlisted <span className="status-count">({statusCounts.shortlisted})</span>
                </button>
                <button
                  className={`status-tab ${applicationFilters.status === 'rejected' ? 'active' : ''}`}
                  onClick={() => handleFilterChange('status', 'rejected')}
                >
                  Rejected <span className="status-count">({statusCounts.rejected})</span>
                </button>
              </div>
              {applicationPagination && (
                <div className="applications-count">
                  Showing {((currentApplicationPage - 1) * (applicationPagination.per_page || 10)) + 1} - {Math.min(currentApplicationPage * (applicationPagination.per_page || 10), applicationPagination.total || 0)} of {applicationPagination.total || 0} applications
                </div>
              )}
            </div>

            {loadingApplications ? (
              <div className="applications-loading">
                <div className="applications-spinner"></div>
                <span>Loading applications...</span>
              </div>
            ) : applicationsList.length === 0 ? (
              <div className="no-applications">
                <div className="no-applications-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <h3>No applications yet</h3>
                <p>Applications will appear here when candidates apply for this job</p>
              </div>
            ) : (
              applicationsList.map((application) => (
                <div key={application.id} className="application-card">
                  <div className="application-header">
                    <div className="application-user">
                      <div className="application-avatar">
                        {application.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="application-user-info">
                        <div className="application-user-name-row">
                          <h4>{application.user?.name || 'Anonymous'}</h4>
                          {application.user?.username ? (
                            <button
                              className="application-profile-link"
                              onClick={() => handleViewProfile(application.user.username)}
                              title={`View ${application.user.name}'s public profile`}
                              type="button"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                              </svg>
                              View Profile
                            </button>
                          ) : (
                            <span className="application-no-username" title="User has not set up a public profile">
                              No Profile
                            </span>
                          )}
                        </div>
                        <span className="application-date">{formatDate(application.created_at || application.applied_at)}</span>
                      </div>
                    </div>
                    <span className={`application-status ${application.status}`}>
                      {application.status}
                    </span>
                  </div>
                  {application.cover_letter && (
                    <div className="application-cover-letter">
                      <p>{application.cover_letter}</p>
                    </div>
                  )}
                  
                  {/* Custom Questions Answers */}
                  {application.meta_responses && Array.isArray(application.meta_responses) && application.meta_responses.length > 0 && (
                    <div className="application-meta-responses" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>
                        Additional Questions
                      </h4>
                      {application.meta_responses.map((metaResponse) => {
                        const field = metaResponse.meta_field || metaResponse.metaField;
                        if (!field) return null;
                        
                        const response = metaResponse.response;
                        const selectedOption = metaResponse.selected_option || metaResponse.selectedOption;
                        const responseOptions = metaResponse.response_options || metaResponse.responseOptions || [];
                        const hasResponse = response || selectedOption || (responseOptions && Array.isArray(responseOptions) && responseOptions.length > 0);
                        
                        if (!hasResponse) {
                          return null;
                        }
                        
                        return (
                          <div key={metaResponse.id} style={{ marginBottom: '20px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#6b7280' }}>
                              {field.question}
                            </div>
                            <div style={{ 
                              padding: '12px 16px', 
                              backgroundColor: '#f9fafb', 
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb'
                            }}>
                              {field.type === 'checkbox' && responseOptions && Array.isArray(responseOptions) && responseOptions.length > 0 ? (
                                <div>
                                  {responseOptions.map((respOpt, idx) => {
                                    const option = respOpt.option || respOpt;
                                    const optionText = option?.option_text || option?.optionText || option;
                                    return (
                                      <div key={idx} style={{ marginBottom: '4px', fontSize: '14px', color: '#374151' }}>
                                        • {optionText}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (field.type === 'dropdown' || field.type === 'radio') && selectedOption ? (
                                <div style={{ fontSize: '14px', color: '#374151' }}>
                                  {selectedOption.option_text || selectedOption.optionText}
                                </div>
                              ) : field.type === 'rich_text' && response ? (
                                <div 
                                  dangerouslySetInnerHTML={{ __html: response }} 
                                  style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}
                                />
                              ) : response ? (
                                <div style={{ fontSize: '14px', color: '#374151', whiteSpace: 'pre-wrap' }}>
                                  {response}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Rejection Reason Display for Job Poster */}
                  {application.status === 'rejected' && application.rejection_reason && (
                    <div className="application-notes-section" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', margin: '0 0 12px 0' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle', color: '#dc2626' }}>
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        Rejection Reason (Sent to Candidate)
                      </h4>
                      <div style={{
                        padding: '16px',
                        background: '#fff3cd',
                        borderLeft: '4px solid #ffc107',
                        borderRadius: '8px',
                        color: '#856404',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {application.rejection_reason.split('\n\n').map((paragraph, index) => (
                          <p key={index} style={{ margin: index > 0 ? '12px 0 0 0' : '0' }}>
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Internal Notes for Shortlisted Candidates */}
                  {application.status === 'shortlisted' && (
                    <div className="application-notes-section" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', margin: 0 }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }}>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14,2 14,8 20,8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                            <polyline points="10,9 9,9 8,9"></polyline>
                          </svg>
                          Internal Notes
                        </h4>
                        {!editingNotes[application.id] && (
                          <button
                            className="application-action-btn"
                            onClick={() => {
                              setEditingNotes(prev => ({ ...prev, [application.id]: true }));
                              setNotesValue(prev => ({ ...prev, [application.id]: application.notes || '' }));
                            }}
                            style={{ fontSize: '12px', padding: '4px 12px' }}
                          >
                            {application.notes ? 'Edit' : 'Add Notes'}
                          </button>
                        )}
                      </div>
                      {editingNotes[application.id] ? (
                        <div>
                          <textarea
                            value={notesValue[application.id] || ''}
                            onChange={(e) => setNotesValue(prev => ({ ...prev, [application.id]: e.target.value }))}
                            placeholder="Add internal notes about this candidate (only visible to you)..."
                            rows={4}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical',
                              minHeight: '80px'
                            }}
                          />
                          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                            <button
                              className="application-action-btn"
                              onClick={() => handleNotesUpdate(application.id, notesValue[application.id] || '')}
                              disabled={updatingStatus[application.id]}
                              style={{ fontSize: '12px', padding: '6px 16px' }}
                            >
                              {updatingStatus[application.id] ? 'Saving...' : 'Save Notes'}
                            </button>
                            <button
                              className="application-action-btn reject-btn"
                              onClick={() => {
                                setEditingNotes(prev => ({ ...prev, [application.id]: false }));
                                setNotesValue(prev => ({ ...prev, [application.id]: application.notes || '' }));
                              }}
                              style={{ fontSize: '12px', padding: '6px 16px' }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          fontSize: '14px',
                          color: application.notes ? '#374151' : '#9ca3af',
                          whiteSpace: 'pre-wrap',
                          minHeight: '40px'
                        }}>
                          {application.notes || 'No notes added yet. Click "Add Notes" to add internal notes about this candidate.'}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="application-actions">
                    {application.resume && (
                      <a 
                        href={application.resume?.file_url || application.resume} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="application-resume-link"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14,2 14,8 20,8"></polyline>
                        </svg>
                        View Resume
                      </a>
                    )}
                    <button
                      className="application-action-btn"
                      onClick={() => handleStartChat(application)}
                      title="Start conversation with candidate"
                      style={{ marginRight: '8px' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                      </svg>
                      Message
                    </button>
                    <div className="application-status-actions">
                      {application.status !== 'shortlisted' && (
                        <button
                          className="application-action-btn shortlist-btn"
                          onClick={() => handleStatusUpdate(application.id, 'shortlisted')}
                          disabled={updatingStatus[application.id]}
                          title="Mark as shortlisted"
                        >
                          {updatingStatus[application.id] ? (
                            <span>Updating...</span>
                          ) : (
                            <>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                              </svg>
                              Shortlist
                            </>
                          )}
                        </button>
                      )}
                      {application.status !== 'rejected' && (
                        <button
                          className="application-action-btn reject-btn"
                          onClick={() => handleStatusUpdate(application.id, 'rejected')}
                          disabled={updatingStatus[application.id]}
                          title="Mark as rejected"
                        >
                          {updatingStatus[application.id] ? (
                            <span>Updating...</span>
                          ) : (
                            <>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="15" y1="9" x2="9" y2="15"></line>
                                <line x1="9" y1="9" x2="15" y2="15"></line>
                              </svg>
                              Reject
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Pagination */}
            {applicationPagination && applicationPagination.last_page > 1 && (
              <div className="applications-pagination">
                <button
                  className="applications-pagination-btn"
                  onClick={() => handlePageChange(currentApplicationPage - 1)}
                  disabled={currentApplicationPage === 1 || loadingApplications}
                >
                  Previous
                </button>
                <span className="applications-pagination-info">
                  Page {currentApplicationPage} of {applicationPagination.last_page}
                </span>
                <button
                  className="applications-pagination-btn"
                  onClick={() => handlePageChange(currentApplicationPage + 1)}
                  disabled={currentApplicationPage === applicationPagination.last_page || loadingApplications}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    {/* Rejection Modal - Rendered via Portal */}
    {showRejectionModal && createPortal(
      <div className="rejection-modal-overlay" onClick={() => {
        setShowRejectionModal(false);
        setRejectingApplicationId(null);
        setSelectedRejectionReason('');
        setCustomRejectionMessage('');
      }}>
        <div className="rejection-modal" onClick={(e) => e.stopPropagation()}>
          <div className="rejection-modal-header">
            <button 
              className="rejection-modal-close"
              onClick={() => {
                setShowRejectionModal(false);
                setRejectingApplicationId(null);
                setSelectedRejectionReason('');
                setCustomRejectionMessage('');
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <h3>REJECT APPLICATION</h3>
            <div style={{ width: '36px' }}></div>
          </div>
          
          <div className="rejection-modal-content">
            <p className="rejection-modal-description">
              Please provide a reason for rejection. You can select a predefined reason or write a custom message.
            </p>

            <div className="rejection-reasons-section">
              <label className="rejection-label">Predefined Reasons (Optional):</label>
              <div className="rejection-reasons-list">
                {[
                  'Does not meet the required qualifications',
                  'Experience level does not match the role',
                  'Skills do not align with job requirements',
                  'Location/work arrangement mismatch',
                  'Salary expectations not aligned',
                  'Found a more suitable candidate',
                  'Position has been filled',
                  'Application received after deadline'
                ].map((reason, index) => (
                  <label key={index} className="rejection-reason-option">
                    <input
                      type="radio"
                      name="rejectionReason"
                      value={reason}
                      checked={selectedRejectionReason === reason}
                      onChange={(e) => setSelectedRejectionReason(e.target.value)}
                    />
                    <span>{reason}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="rejection-custom-section">
              <label className="rejection-label">
                Custom Message {selectedRejectionReason ? '(Optional)' : '(Required)'}:
              </label>
              <textarea
                className="rejection-custom-textarea"
                value={customRejectionMessage}
                onChange={(e) => setCustomRejectionMessage(e.target.value)}
                placeholder="Add a personalized message to the candidate..."
                rows="4"
              />
            </div>
          </div>

          <div className="rejection-modal-footer">
            <button
              className="rejection-modal-cancel"
              onClick={() => {
                setShowRejectionModal(false);
                setRejectingApplicationId(null);
                setSelectedRejectionReason('');
                setCustomRejectionMessage('');
              }}
            >
              Cancel
            </button>
            <button
              className="rejection-modal-submit"
              onClick={handleRejectionSubmit}
              disabled={updatingStatus[rejectingApplicationId] || (!selectedRejectionReason && !customRejectionMessage.trim())}
            >
              {updatingStatus[rejectingApplicationId] ? 'Rejecting...' : 'Reject Application'}
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}

    {/* Share Modal - Available on both Details and Applications tabs */}
    {jobDetails && !isAppliedJobsView && (
      <JobShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        jobId={jobDetails.id}
      />
    )}
    </>
  );
};

export default JobDetailPanel;

